import React, { useState, useEffect } from 'react';

const TermsAndConditions: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    container: {
      minHeight: '100vh',
      fontFamily: "'Open Sans', sans-serif",
    },
    header: {
      backgroundColor: '#fff',
      position: 'sticky' as const,
      top: 0,
      zIndex: 1000,
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    },
    nav: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: 'auto',
    },
    logo: {
      fontSize: '26px',
      fontWeight: '700',
      color: '#1a365d',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: 0,
      margin: 0,
      lineHeight: 0,
    },
    hamburger: {
      display: 'none',
      flexDirection: 'column' as const,
      gap: '4px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '5px',
    },
    hamburgerLine: {
      width: '25px',
      height: '3px',
      backgroundColor: '#333',
      transition: 'all 0.3s',
    },
    mobileMenu: {
      display: 'none',
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: '#fff',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      padding: '20px',
      flexDirection: 'column' as const,
      gap: '20px',
    },
    mobileMenuOpen: {
      display: 'flex',
    },
    navLink: {
      color: '#4a5568',
      textDecoration: 'none',
      fontSize: '16px',
      fontWeight: '500',
      transition: 'color 0.3s',
      cursor: 'pointer',
    },
    ctaButton: {
      backgroundColor: '#141D46',
      color: '#fff',
      padding: '12px 28px',
      borderRadius: '4px',
      textDecoration: 'none',
      fontWeight: '600',
      fontFamily: "'Open Sans', sans-serif",
      transition: 'all 0.3s',
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
      boxShadow: '0 2px 4px rgba(20,29,70,0.2)',
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '40px 20px' : '60px 20px',
    },
    footer: {
      backgroundColor: '#1a202c',
      color: '#a0aec0',
      padding: '60px 20px 40px',
      marginTop: '80px',
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
  };

  // Add responsive styles
  if (isMobile) {
    styles.hamburger.display = 'flex';
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        {/* Navigation */}
        <nav style={styles.nav}>
          <a href="/" style={styles.logo}>
            <img src="/assets/onepropertyaustralia/logo.png" alt="One Property Australia" style={{ height: '80px', display: 'block' }} />
          </a>
          
          <img 
            src="/assets/easypropertyinvestorimages/ssllogo.png" 
            alt="SSL Secured" 
            style={{ 
              height: '40px', 
              marginLeft: 'auto',
              marginRight: '20px'
            }} 
          />
          
          <button
            style={styles.hamburger}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span style={styles.hamburgerLine}></span>
            <span style={styles.hamburgerLine}></span>
            <span style={styles.hamburgerLine}></span>
          </button>
        </nav>
        
        {/* Blue bar under nav */}
        <div style={{ 
          width: '100%', 
          height: isMobile ? '45px' : '35px', 
          backgroundColor: '#141D46',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: isMobile ? '14px' : '24px',
          fontWeight: '900',
          fontFamily: "'Open Sans', sans-serif",
          padding: isMobile ? '0 10px' : '0',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}>
          <img 
            src="/assets/easypropertyinvestorimages/star.png" 
            alt="5 stars" 
            style={{ 
              height: isMobile ? '16px' : '20px',
              marginRight: isMobile ? '5px' : '10px',
              flexShrink: 0
            }} 
          />
          <span style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }}>
            Customer Reviews: 5-Star Rated
          </span>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div style={{...styles.mobileMenu, ...styles.mobileMenuOpen}}>
            <a href="/" style={styles.navLink}>Home</a>
            <a href="/privacy-policy" style={styles.navLink}>Privacy Policy</a>
            <a href="/terms-and-conditions" style={styles.navLink}>Terms and Conditions</a>
            <a href="/cookie-policy" style={styles.navLink}>Cookie Policy</a>
            <button style={styles.ctaButton}>Get Started</button>
          </div>
        )}
      </header>

      {/* Main Content */}
      <div style={styles.content}>
        <h1 style={{ 
          fontSize: isMobile ? '32px' : '48px',
          fontWeight: '700',
          color: '#141D46',
          marginBottom: '30px',
          fontFamily: "'Open Sans', sans-serif"
        }}>
          Terms and Conditions
        </h1>
        
        <p style={{ 
          fontSize: '16px',
          color: '#666',
          marginBottom: '20px',
          lineHeight: '1.6'
        }}>
          Last updated: January 2025
        </p>

        <h2 style={{ 
          fontSize: '24px',
          fontWeight: '600',
          color: '#333',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          1. Acceptance of Terms
        </h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
        </p>

        <h2 style={{ 
          fontSize: '24px',
          fontWeight: '600',
          color: '#333',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          2. Use License
        </h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
        </p>
        <ul style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6', paddingLeft: '30px' }}>
          <li>Lorem ipsum dolor sit amet, consectetur adipiscing elit</li>
          <li>Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua</li>
          <li>Ut enim ad minim veniam, quis nostrud exercitation</li>
          <li>Ullamco laboris nisi ut aliquip ex ea commodo consequat</li>
        </ul>

        <h2 style={{ 
          fontSize: '24px',
          fontWeight: '600',
          color: '#333',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          3. Disclaimer
        </h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.
        </p>

        <h2 style={{ 
          fontSize: '24px',
          fontWeight: '600',
          color: '#333',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          4. Limitations
        </h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam.
        </p>

        <h2 style={{ 
          fontSize: '24px',
          fontWeight: '600',
          color: '#333',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          5. Intellectual Property Rights
        </h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident, similique sunt in culpa qui officia deserunt mollitia animi, id est laborum et dolorum fuga.
        </p>

        <h2 style={{ 
          fontSize: '24px',
          fontWeight: '600',
          color: '#333',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          6. Termination
        </h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          Et harum quidem rerum facilis est et expedita distinctio. Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus, omnis voluptas assumenda est, omnis dolor repellendus. Temporibus autem quibusdam et aut officiis debitis.
        </p>

        <h2 style={{ 
          fontSize: '24px',
          fontWeight: '600',
          color: '#333',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          7. Governing Law
        </h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          These Terms and Conditions are governed by and construed in accordance with the laws of Queensland, Australia. You irrevocably submit to the exclusive jurisdiction of the courts in that State or location.
        </p>

        <h2 style={{ 
          fontSize: '24px',
          fontWeight: '600',
          color: '#333',
          marginTop: '40px',
          marginBottom: '20px'
        }}>
          8. Contact Information
        </h2>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px', lineHeight: '1.6' }}>
          If you have any questions about these Terms and Conditions, please contact us at:<br />
          <br />
          One Property Australia Pty Ltd<br />
          Email: legal@onepropertyaustralia.com.au<br />
          Phone: 1300 XXX XXX<br />
          Address: Brisbane, Queensland, Australia
        </p>
      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={{
            color: '#666',
            fontSize: isMobile ? '10px' : '12px',
            lineHeight: '1.6',
            textAlign: 'justify',
            margin: '0 auto',
            padding: isMobile ? '0 10px' : '0'
          }}>
            <p>
              <a href="https://onepropertyaustralia.com.au" style={{ color: '#a7a7a7', fontWeight: 'bold', textDecoration: 'none' }}>onepropertyaustralia.com.au</a> is owned and operated by One Property Australia Pty Ltd (ABN 27 147 278 153) (referred to as "we", "us", or "One Property Australia"). The website may share your details with independent property and real estate consultants who may contact you to provide the services you have requested. You are not obligated to accept or engage with any of these services. By submitting your information through <a href="https://onepropertyaustralia.com.au" style={{ color: '#a7a7a7', fontWeight: 'bold', textDecoration: 'none' }}>onepropertyaustralia.com.au</a>, you consent to us providing your details to third parties and to being contacted by phone, SMS, or email regarding your enquiry. Please review our Privacy Policy for more details on how we manage and protect your information.
            </p>
            <p style={{ marginTop: '10px' }}>
              We do not provide financial product advice or financial dealing services, nor do we endorse financial products or issuers. Our role is to connect your enquiry with a third-party provider that may be able to offer comparisons across a variety of products, services, and providers. Before engaging any provider, you should carefully assess whether their services are appropriate for your financial situation, needs, and goals, and review their disclosure documents (such as their financial services guide). If you choose to proceed with a service offered after being referred via <a href="https://onepropertyaustralia.com.au" style={{ color: '#a7a7a7', fontWeight: 'bold', textDecoration: 'none' }}>onepropertyaustralia.com.au</a>, your dealings will be directly with that provider and not with One Property Australia Pty Ltd.
            </p>
            <p style={{ marginTop: '10px' }}>
              We are not involved in delivering financial advice and take no responsibility for the services provided by third-party providers. To the fullest extent permitted by law, we are not liable for any loss or damage arising from your reliance on or use of services delivered by external providers. We also make no representation as to the quality, accuracy, or suitability of any financial advice offered by these third parties.
            </p>
            <p style={{ marginTop: '10px' }}>
              One Property Australia Pty Ltd may receive a fixed referral fee when your details are passed on to relevant providers. This fee is not commission-based, is not dependent on the outcome of your enquiry, and does not vary based on any investment decisions you may make. While we aim to ensure all information presented on our website is current and accurate, there may be times when updates are required and some details may be temporarily out of date.
            </p>
          </div>
          
          <p style={{
            color: '#666',
            fontSize: '14px',
            margin: '30px 0 0 0',
            textAlign: 'center'
          }}>
            © 2025 One Property Australia. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;